/**
 * @class OverlayHoverOpenerDirective
 * @description A directive that attaches event listeners to open and close an overlay panel on hover or click.
 */
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { PrimeFlexBreakpoints } from '@osos/shared/types';
import { DomHandler } from 'primeng/dom';
import { Popover } from 'primeng/popover';
import { fromEvent, of, takeUntil } from 'rxjs';
import { concatMap, delay, switchMap, take } from 'rxjs/operators';

let overlayClassIndex = 0;
@Directive({
  selector: '[appOverlayHoverOpener]',
  standalone: true,
})
export class OverlayHoverOpenerDirective
  implements AfterViewInit, OnDestroy, OnChanges
{
  /**
   * The overlay panel to be opened.
   * @type {OverlayPanel}
   */
  @Input('appOverlayHoverOpener') overlayPanel: Popover;
  /**
   * Determines if the overlay shown on click.
   * @type {boolean}
   * @default true
   */
  @Input() clickable = true;
  /**
   * Determines if the overlay is shown when click on mobile devices.
   * @type {boolean}
   * @default true
   */
  @Input() clickableOnMobile = true;
  /**
   * Determines if hovering is enabled.
   * @type {boolean}
   * @default true
   */
  @Input() hoverEnabled = true;
  /**
   * Determines if hovering is enabled.
   * @type {boolean}
   * @default true
   */
  @Input() appOverlayHoverOpenerDisabled = false;

  /**
   * Determines if overlay is hidden when we click the opener.
   * This will work only if clickable is false
   * @type {boolean}
   * @default true
   */
  @Input() closableOnClick = false;

  private mouseEnterListener: Function | null;
  private mouseLeaveListener: Function | null;
  private clickListener: Function | null;
  private windowResizeListener: Function | null;

  private overlayTargetClass = `overlay-hover-opened-${overlayClassIndex++}`;

  private toggleOverlay: (event: any) => boolean | void = (event) => {
    this.overlayPanel.toggle(event);
    this.overlayPanel.cd.detectChanges();
  };

  private openOverlay: (event: any) => boolean | void = (event) => {
    this.overlayPanel.show(event);
    this.overlayPanel.cd.detectChanges();
  };

  private hideOverlay: (event?: any) => boolean | void = () => {
    this.overlayPanel.hide();
    this.overlayPanel.cd.detectChanges();
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private el: ElementRef,
    private zone: NgZone,
    private renderer: Renderer2,
    private breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /*
   * Attaches event listeners to the overlay and window resize events.
   * @returns {void}
   */
  ngAfterViewInit(): void {
    this.attachListeners();
  }
  // TODO: Reset the event listeners on each input change, for now we assume that the inputs is evaluated once only
  /**
   * @param {SimpleChanges} changes - The changes object containing all input changes.
   * @returns {void}
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.overlayPanel) {
      if (this.overlayPanel) {
        const oldClasses = this.overlayPanel.styleClass || '';
        this.overlayPanel.styleClass = `${oldClasses} ${this.overlayTargetClass}`;
      }
    }
    if (
      changes.appOverlayHoverOpenerDisabled &&
      !changes.appOverlayHoverOpenerDisabled.firstChange
    ) {
      this.clearListeners();
      if (!this.appOverlayHoverOpenerDisabled) this.attachListeners();
    }
  }
  /**
   * Attaches a window resize listener to clear opener mouse event and opener click listeners and reattach overlay event listeners.
   * @returns {void}
   */
  private attachWindowResizeEvents(): void {
    if (!!this.overlayPanel && !this.appOverlayHoverOpenerDisabled) {
      if (isPlatformBrowser(this.platformId)) {
        this.windowResizeListener = this.renderer.listen(
          'window',
          'resize',
          () => {
            this.clearOpenerMouseEventListeners();
            this.clearOpenerClickEventListener();
            this.attachOpenerEventListeners();
          },
        );
      }
    }
  }
  /**
   * Attaches mouseenter and click listeners to the opener element.
   * @returns {void}
   */
  private attachOpenerEventListeners(): void {
    if (!!this.overlayPanel && !this.appOverlayHoverOpenerDisabled) {
      if (isPlatformBrowser(this.platformId)) {
        this.zone.runOutsideAngular(() => {
          if (!this.mouseEnterListener && !this.isMobileOrTouchScreenScreen()) {
            this.mouseEnterListener = this.renderer.listen(
              this.el.nativeElement,
              'mouseenter',
              (event: Event) => {
                this.openOverlay(event);
                this.attachMouseLeaveForOpenerAndOverlayPanel();
              },
            );
          }

          if (
            !this.clickListener &&
            (this.clickable || this.doesDeviceAllowClickable())
          ) {
            this.clickListener = this.renderer.listen(
              this.el.nativeElement,
              'click',
              this.toggleOverlay,
            );
          }

          if (!this.clickListener && !this.clickable && this.closableOnClick) {
            this.clickListener = this.renderer.listen(
              this.el.nativeElement,
              'click',
              this.hideOverlay,
            );
          }
        });
      }
    }
  }
  /**
   * Attaches a mouseleave listener to the overlay panel and opener.
   * @returns {void}
   */
  private attachMouseLeaveForOpenerAndOverlayPanel(): void {
    const overlayElement: HTMLElement = document.querySelector(
      `.${this.overlayTargetClass}`,
    );

    fromEvent(this.el.nativeElement, 'mouseleave')
      .pipe(
        delay(100),
        concatMap(() =>
          fromEvent(this.document.documentElement, 'mousemove').pipe(take(1)),
        ),
        switchMap((event: Event) => {
          if (
            (event.target as HTMLElement).closest(`.${this.overlayTargetClass}`)
          ) {
            return fromEvent(overlayElement, 'mouseleave');
          }
          return of(null);
        }),
        take(1),
        takeUntil(fromEvent(this.el.nativeElement, 'mouseenter')),
      )
      .subscribe(() => {
        this.hideOverlay();
      });
  }

  /**
   * Attaches all event listeners.
   * @returns {void}
   */
  private attachListeners(): void {
    this.attachOpenerEventListeners();
    this.attachWindowResizeEvents();
  }

  /**
   * Clears all event listeners.
   * @returns {void}
   */
  private clearListeners(): void {
    this.clearOpenerClickEventListener();
    this.clearOpenerMouseEventListeners();
    this.clearWindowResizeListener();
  }

  /**
   * Clears opener mouse event listeners.
   * @returns {void}
   */
  private clearOpenerMouseEventListeners(): void {
    this.mouseEnterListener?.();
    this.mouseLeaveListener?.();
    this.mouseEnterListener = null;
    this.mouseLeaveListener = null;
  }

  /*
   * Clears opener click listener.
   * @returns {void}
   */
  private clearOpenerClickEventListener(): void {
    this.clickListener?.();
    this.clickListener = null;
  }

  /**
   * Clears window resize listener.
   * @returns {void}
   */
  private clearWindowResizeListener(): void {
    this.windowResizeListener?.();
    this.windowResizeListener = null;
  }

  /**
   * Checks if the device allows clickable overlays.
   * @returns {boolean} - True if the device is a mobile or touchscreen and clickableOnMobile is true, false otherwise.
   */
  private doesDeviceAllowClickable(): boolean {
    return this.isMobileOrTouchScreenScreen() && this.clickableOnMobile;
  }

  /**
   * Checks if the device is a mobile or touchscreen.
   * @returns {boolean} - True if the device is a mobile or touchscreen, false otherwise.
   */
  private isMobileOrTouchScreenScreen(): boolean {
    return (
      DomHandler.isTouchDevice() ||
      this.breakpointObserver.isMatched([
        PrimeFlexBreakpoints.Small,
        PrimeFlexBreakpoints.XSmall,
        PrimeFlexBreakpoints.Medium,
      ])
    );
  }

  /**
   * Clears all event listeners when the directive is destroyed.
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.clearListeners();
  }
}
